"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import { KbvPluginService } from "@/services/kbvPluginService";
import { useRemoteLeagueSearch, fetchHotLeagues } from "@/hooks/useRemoteLeagueSearch";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useCompConfig } from "@/context/CompConfigContext";
import { buildThemeStyle } from "./buildThemeStyle";
import Loading from "@/components/Loading";
import NoData from "@/components/NoData";
import { TAB_LABELS } from "@/constants";
import "./index.css";

const getGroupLabel = (group, index) => {
  const key = group.groupName || `group_${group.id}`;
  if (TAB_LABELS[key]) return TAB_LABELS[key];

  const letter = String(key).match(/(?:group[_\s]*)([a-z])$/i)?.[1]?.toUpperCase();
  if (letter) return `Bảng ${letter}`;

  const raw = group.groupName || `Bảng ${String.fromCharCode(65 + index)}`;
  return raw.replace(/\bGroup\b/gi, "Bảng");
};

const getCupRankClass = (rank) => {
  if (rank <= 2) return "bg-[var(--kbv-bxhv3-qualify,#01c28e)] text-white";
  if (rank === 3) return "bg-[var(--kbv-bxhv3-eliminate,#ff4a2b)] text-white";
  return "bg-[var(--kbv-bxhv3-rank-neutral,#64748b)] text-white";
};

export default function BangXepHangPageV3() {
  const COLLAPSED_ITEMS_COUNT = 10;
  const compConfig = useCompConfig();
  const { hasDataConfig } = compConfig;
  const themeStyle = buildThemeStyle(compConfig, hasDataConfig);

  const [hotLeagues, setHotLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);

  const [standingsData, setStandingsData] = useState(null);
  const [isStandingsLoading, setIsStandingsLoading] = useState(false);

  const dropdownRef = useRef(null);
  const debouncedQuery = useDebouncedValue(searchQuery, 350).trim();

  const { results: remoteLeagues, isLoading: isSearchLoading } = useRemoteLeagueSearch({
    enabled: isDropdownOpen,
    keyword: debouncedQuery,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await fetchHotLeagues();
      if (cancelled) return;
      setHotLeagues(list || []);
      if (list && list.length > 0 && !selectedLeague) {
        const defaultLeague = list.find((league) => Number(league?.id) === 1572) || list[0];
        setSelectedLeague(defaultLeague);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedLeague?.id) return;

    let cancelled = false;
    setIsStandingsLoading(true);
    (async () => {
      try {
        const data = await KbvPluginService.getLeagueStandings(selectedLeague.id);
        if (cancelled) return;
        setStandingsData(data?.result);
      } catch (error) {
        console.error("Fetch standings error:", error);
        setStandingsData(null);
      } finally {
        if (!cancelled) setIsStandingsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [selectedLeague?.id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const displayLeagues = useMemo(() => {
    if (debouncedQuery) return remoteLeagues;
    return hotLeagues;
  }, [debouncedQuery, remoteLeagues, hotLeagues]);

  const isCup = useMemo(
    () =>
      Array.isArray(standingsData?.roundScoreItems) &&
      standingsData.roundScoreItems.length > 0,
    [standingsData],
  );

  const cupGroups = useMemo(() => {
    if (!isCup) return [];
    return standingsData.roundScoreItems[0]?.groupScoreItems || [];
  }, [isCup, standingsData]);

  useEffect(() => {
    setActiveGroupIndex(0);
    setIsExpanded(false);
  }, [selectedLeague?.id, standingsData]);

  const handleSelect = (league) => {
    setSelectedLeague(league);
    setIsDropdownOpen(false);
    setSearchQuery("");
    setIsExpanded(false);
    setActiveGroupIndex(0);
  };

  const renderTable = () => {
    if (isStandingsLoading) {
      return (
        <div className="flex justify-center py-10">
          <Loading />
        </div>
      );
    }

    if (!standingsData) return null;

    const list = isCup
      ? cupGroups[activeGroupIndex]?.scoreItems || []
      : standingsData?.totalStandings || [];

    if (list.length === 0) return (
      <NoData>
        <span className="text-xs text-[var(--theme-text-muted)]">Không có dữ liệu bảng xếp hạng</span>
      </NoData>
    );

    const visibleList = isExpanded ? list : list.slice(0, COLLAPSED_ITEMS_COUNT);
    const showToggleButton = !isCup && list.length > COLLAPSED_ITEMS_COUNT;

    if (isCup) {
      const sortedCupList = [...list].sort((a, b) => {
        const pointsA = Number(a.integral ?? a.winCount * 3 + a.drawCount);
        const pointsB = Number(b.integral ?? b.winCount * 3 + b.drawCount);
        if (pointsB !== pointsA) return pointsB - pointsA;

        const gdA = Number(a.goalDifference ?? a.totalGoalDifference ?? 0);
        const gdB = Number(b.goalDifference ?? b.totalGoalDifference ?? 0);
        if (gdB !== gdA) return gdB - gdA;

        return Number(b.getScore ?? 0) - Number(a.getScore ?? 0);
      });

      return (
        <>
          <div className="grid grid-cols-4 gap-1.5">
            {cupGroups.map((group, index) => (
              <button
                key={group.id || index}
                type="button"
                onClick={() => setActiveGroupIndex(index)}
                className={`kbv-bxhv3-group-btn rounded-full px-2 py-0.5 text-center text-[10px] font-semibold ${
                  activeGroupIndex === index ? "kbv-bxhv3-group-btn--active" : ""
                }`}
              >
                {getGroupLabel(group, index)}
              </button>
            ))}
          </div>

          <div className="kbv-bxhv3-table-wrap mt-2 w-full overflow-hidden rounded-lg border">
            <div className="w-full overflow-x-auto">
              <table className="w-full min-w-[240px] table-fixed border-separate border-spacing-0 text-[11px] sm:text-[12px]">
                <thead>
                  <tr className="kbv-bxhv3-thead">
                    <th className="w-[65%] rounded-tl-lg py-2 font-semibold">
                      <div className="flex items-center">
                        <span className="inline-flex h-5 w-5 shrink-0" aria-hidden="true" />
                        <span className="flex-1 text-center ml-[34px]">Đội bóng</span>
                      </div>
                    </th>
                    <th className="w-[20%] py-2 text-center font-semibold">Trận</th>
                    <th className="w-[15%] rounded-tr-lg py-2 text-center font-semibold">Điểm</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCupList.map((row, idx) => {
                    const team = row.teams || {};
                    const rankNumber = idx + 1;
                    const points = row.integral ?? row.winCount * 3 + row.drawCount;
                    const isTopOne = rankNumber === 1;

                    return (
                      <tr
                        key={row.id || idx}
                        className={`kbv-bxhv3-row border-b transition-colors ${isTopOne ? "kbv-bxhv3-row--top" : ""}`}
                      >
                        <td className="border-b border-[var(--theme-border)] py-2 pr-1">
                          <div className="flex items-center gap-1.5">
                            <span
                              className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${getCupRankClass(rankNumber)}`}
                            >
                              {rankNumber}
                            </span>
                            <div className="grid min-w-0 flex-1 grid-cols-[1fr_16px_minmax(0,1fr)] items-center gap-x-1">
                              <span aria-hidden="true" />
                              <div
                                role="link"
                                tabIndex={0}
                                onClick={() => window.location.assign(`/doi-bong/${team.slug || team.id}`)}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    window.location.assign(`/doi-bong/${team.slug || team.id}`);
                                  }
                                }}
                                className="kbv-bxhv3-team-link group contents cursor-pointer"
                              >
                                <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                                  {team.logo && (
                                    <img
                                      src={team.logo}
                                      alt=""
                                      width={16}
                                      height={16}
                                      className="h-4 w-4 object-contain"
                                    />
                                  )}
                                </span>
                                <span className={`kbv-bxhv3-team-name truncate text-left text-[11px] transition-colors sm:text-[12px] ${isTopOne ? "font-bold text-[var(--kbv-bxhv3-top-rank,#FFDF58)]" : "font-semibold text-[var(--theme-text)]"}`}>
                                  {team.nameVi || team.name}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className={`border-b border-[var(--theme-border)] py-2 text-center font-medium ${isTopOne ? "font-bold text-[var(--kbv-bxhv3-top-rank,#FFDF58)]" : "text-[var(--theme-text)]"}`}>
                          {row.totalCount}
                        </td>
                        <td className={`border-b border-[var(--theme-border)] py-2 text-center text-[12px] sm:text-[13px] ${isTopOne ? "font-extrabold text-[var(--kbv-bxhv3-top-rank,#FFDF58)]" : "font-bold text-[var(--theme-text)]"}`}>
                          {points}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="kbv-bxhv3-table-wrap w-full overflow-hidden rounded-lg border">
          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-[340px] table-fixed border-separate border-spacing-0 text-[11px] sm:min-w-0 sm:text-[12px]">
              <thead>
                <tr className="kbv-bxhv3-thead">
                  <th className="w-[10%] rounded-tl-lg py-2 text-center font-semibold">#</th>
                  <th className="w-[50%] py-2 pl-2 text-left font-semibold">Đội bóng</th>
                  <th className="w-[13%] py-2 text-center font-semibold">P</th>
                  <th className="w-[13%] py-2 text-center font-semibold">GD</th>
                  <th className="w-[14%] rounded-tr-lg py-2 text-center font-semibold">PTS</th>
                </tr>
              </thead>
              <tbody>
                {visibleList.map((row, idx) => {
                  const team = row.teams || {};
                  const goalDiff = Number(row.goalDifference ?? row.totalGoalDifference ?? row.goalsDiff ?? 0);
                  const rankNumber = Number(row.rank ?? idx + 1);
                  const isTopOne = rankNumber === 1;
                  const isRelegation = rankNumber >= list.length - 2;
                  return (
                    <tr
                      key={row.id || idx}
                      className={`kbv-bxhv3-row border-b transition-colors ${isTopOne ? "kbv-bxhv3-row--top" : ""} ${isRelegation ? "kbv-bxhv3-row--relegation" : ""}`}
                    >
                      <td className={`border-b border-[var(--theme-border)] py-2 text-center text-[10px] font-medium leading-none ${isTopOne ? "text-[var(--kbv-bxhv3-top-rank,#FFDF58)]" : ""} ${isRelegation ? "text-[var(--kbv-bxhv3-relegation,#ff4a2b)]" : "text-[var(--theme-text)]"}`}>
                        {row.rank}
                      </td>
                      <td className="max-w-0 border-b border-[var(--theme-border)] py-2 pr-1">
                        <div
                          role="link"
                          tabIndex={0}
                          onClick={() => window.location.assign(`/doi-bong/${team.slug || team.id}`)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              window.location.assign(`/doi-bong/${team.slug || team.id}`);
                            }
                          }}
                          className="kbv-bxhv3-team-link group flex w-full cursor-pointer items-center gap-1.5"
                        >
                          {team.logo && (
                            <img
                              src={team.logo}
                              alt=""
                              width={16}
                              height={16}
                              className="shrink-0 object-contain"
                            />
                          )}
                          <span className="kbv-bxhv3-team-name truncate text-[11px] font-semibold text-[var(--theme-text)] transition-colors sm:text-[12px]">
                            {team.name}
                          </span>
                        </div>
                      </td>
                      <td className="border-b border-[var(--theme-border)] py-2 text-center font-medium text-[var(--theme-text)]">
                        {row.totalCount}
                      </td>
                      <td className={`border-b border-[var(--theme-border)] py-2 text-center font-medium ${goalDiff >= 0 ? "text-[var(--kbv-bxhv3-gd-up,#01c28e)]" : "text-[var(--kbv-bxhv3-gd-down,#ff4a2b)]"}`}>
                        {goalDiff > 0 ? `+${goalDiff}` : goalDiff}
                      </td>
                      <td className="border-b border-[var(--theme-border)] py-2 text-center text-[12px] font-bold text-[var(--theme-text)] sm:text-[13px]">
                        {row.integral}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        {showToggleButton && (
          <button
            type="button"
            onClick={() => setIsExpanded((prev) => !prev)}
            className="kbv-bxhv3-expand-btn mt-2 w-full rounded-lg border py-2 text-[13px] font-semibold transition-colors sm:text-[14px]"
          >
            {isExpanded ? "Thu gọn" : "Mở rộng"}
          </button>
        )}
      </>
    );
  };

  return (
    <aside
      className="kbv-bxhv3 min-h-87.5 w-full self-start overflow-hidden rounded-xl border border-[var(--theme-border)] bg-[var(--theme-foreground)] shadow-sm"
      style={themeStyle}
      aria-label="Ranking per tournament"
    >
      <div className="flex flex-col gap-2 p-2 sm:p-2.5">
        
        <h2 className="text-[16px] font-bold text-[var(--theme-text)] sm:text-[17px]">Bảng xếp hạng</h2>
        

        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="kbv-bxhv3-trigger flex h-8 w-full items-center justify-between rounded-full border px-3 text-[13px] transition-all sm:px-4 sm:text-[14px]"
          >
            <div className="flex items-center gap-2 overflow-hidden">
              {selectedLeague?.logo && (
                <img
                  src={selectedLeague.logo}
                  alt=""
                  width={14}
                  height={14}
                  className="object-contain"
                />
              )}
              <span className="truncate font-medium text-[var(--theme-text)]">
                {selectedLeague?.nameVN || selectedLeague?.name || "Chọn giải đấu..."}
              </span>
            </div>
            <img
              src="/arrow-up.svg"
              alt=""
              width={16}
              height={16}
              className={`kbv-bxhv3-trigger-arrow transition-transform duration-200 ${isDropdownOpen ? "rotate-0" : "rotate-180"}`}
            />
          </button>

          {isDropdownOpen && (
            <div className="kbv-bxhv3-dropdown absolute left-0 right-0 top-[40px] z-[100] overflow-hidden rounded-xl">
              <div className="kbv-bxhv3-search-wrap border-b p-2.5">
                <div className="relative">
                  <input
                    autoFocus
                    type="text"
                    placeholder="Tìm kiếm giải đấu..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="kbv-bxhv3-search-input h-9 w-full rounded-lg border pl-9 pr-3 text-[13px] outline-none transition-all placeholder:text-[var(--theme-text-muted)] sm:text-[14px]"
                  />
                  <img
                    src="/search.svg"
                    alt=""
                    width={14}
                    height={14}
                    className="kbv-bxhv3-search-icon pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
                  />
                </div>
              </div>
              <div className="kbv-bxhv3-league-list max-h-[50vh] overflow-y-auto py-1 sm:max-h-[240px]">
                {isSearchLoading ? (
                  <div className="flex justify-center p-4"><Loading /></div>
                ) : displayLeagues.map((league) => (
                  <button
                    key={league.id}
                    type="button"
                    onClick={() => handleSelect(league)}
                    className={`kbv-bxhv3-league-item flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition-colors sm:px-3.5 ${selectedLeague?.id === league.id ? "kbv-bxhv3-league-item--active" : "text-[var(--theme-text)]"}`}
                  >
                    {league.logo && (
                      <img
                        src={league.logo}
                        alt=""
                        width={16}
                        height={16}
                        className="object-contain"
                      />
                    )}
                    <span className={`truncate text-[13px] sm:text-[14px] ${selectedLeague?.id === league.id ? "font-semibold" : "font-medium"}`}>{league.nameVN || league.name}</span>
                    {selectedLeague?.id === league.id && (
                      <img
                        src="/icons/check-icon.svg"
                        alt=""
                        width={14}
                        height={14}
                        className="ml-auto opacity-90"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          {renderTable()}
        </div>
      </div>
    </aside>
  );
}
